// Tiny deterministic 2D value-noise + warp helpers.
// No external deps. Octaved fbm gives nice organic shapes for backgrounds.

import { mulberry32 } from './rng';

/** Hash-based 2D value noise. Returns [0, 1]. */
function valueNoise(seed: number) {
  // Pre-generate a 256-entry random table.
  const rand = mulberry32(seed >>> 0);
  const grad = new Float32Array(256);
  for (let i = 0; i < 256; i++) grad[i] = rand();
  const perm = new Uint8Array(512);
  const p = new Uint8Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const t = p[i]!; p[i] = p[j]!; p[j] = t;
  }
  for (let i = 0; i < 512; i++) perm[i] = p[i & 255]!;
  const at = (xi: number, yi: number): number => grad[perm[(xi + perm[yi & 255]!) & 255]!]!;

  const smooth = (t: number): number => t * t * (3 - 2 * t);

  return (x: number, y: number): number => {
    const xi = Math.floor(x), yi = Math.floor(y);
    const xf = x - xi, yf = y - yi;
    const u = smooth(xf), v = smooth(yf);
    const a = at(xi,     yi);
    const b = at(xi + 1, yi);
    const c = at(xi,     yi + 1);
    const d = at(xi + 1, yi + 1);
    return (a * (1 - u) * (1 - v) + b * u * (1 - v) + c * (1 - u) * v + d * u * v);
  };
}

/** Fractional Brownian motion: sums noise octaves of decreasing amplitude
 *  for a more organic / cloud-like shape. Returns [0, 1]. */
export function fbm(seed: number, octaves = 4): (x: number, y: number) => number {
  const n = valueNoise(seed);
  return (x: number, y: number): number => {
    let amp = 0.5;
    let freq = 1;
    let total = 0;
    let norm = 0;
    for (let i = 0; i < octaves; i++) {
      total += amp * n(x * freq, y * freq);
      norm += amp;
      amp *= 0.5;
      freq *= 2;
    }
    return total / norm;
  };
}
